import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AnalysisCard from './AnalysisCard';
import { AnalysisListSkeleton, AnalysesEmptyState } from './AnalysisListSkeleton';
import { useLanguage } from '../../../contexts/LanguageContext';

const AnalysisTable = ({ analyses, onViewDetails, isLoading = false, onStartAnalysis }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const { t } = useLanguage();

  const sortOptions = [
    { value: 'date', label: t('date') },
    { value: 'address', label: t('address') },
    { value: 'suburb', label: t('suburb') },
    { value: 'verdict', label: t('verdict') },
    { value: 'confidence', label: t('confidence') }
  ];

  const filteredAnalyses = analyses?.filter(analysis => {
    const searchLower = searchQuery?.toLowerCase();
    return (analysis?.address?.toLowerCase()?.includes(searchLower) ||
    analysis?.suburb?.toLowerCase()?.includes(searchLower) || analysis?.verdict?.toLowerCase()?.includes(searchLower));
  });

  const sortedAnalyses = [...filteredAnalyses]?.sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'date':
        comparison = new Date(b.date) - new Date(a.date);
        break;
      case 'address':
        comparison = a?.address?.localeCompare(b?.address);
        break;
      case 'suburb':
        comparison = a?.suburb?.localeCompare(b?.suburb);
        break;
      case 'verdict':
        comparison = a?.verdict?.localeCompare(b?.verdict);
        break;
      case 'confidence':
        comparison = (b?.confidenceScore || 0) - (a?.confidenceScore || 0);
        break;
      default:
        comparison = 0;
    }

    return sortOrder === 'asc' ? -comparison : comparison;
  });

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
      <div className="p-4 md:p-5 lg:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl md:text-2xl font-semibold text-foreground font-heading mb-1">
              {t('recentAnalyses')}
            </h2>
            <p className="text-sm text-muted-foreground text-caption">
              {isLoading ? t('loading') : `${sortedAnalyses?.length} ${sortedAnalyses?.length === 1 ? t('propertyAnalyzed') : t('propertiesAnalyzed')}`}
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Input
                type="search"
                placeholder={t('searchByAddress')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <Icon name="Search" size={18} color="var(--color-muted-foreground)" />
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex-1 md:flex-initial md:w-48">
              <Select
                options={sortOptions}
                value={sortBy}
                onChange={setSortBy}
                placeholder={t('sortByLabel')}
              />
            </div>
            <button
              onClick={toggleSortOrder}
              className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-base flex items-center justify-center"
              aria-label={`Sort ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
            >
              <Icon 
                name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
                size={20} 
                color="var(--color-foreground)" 
              />
            </button>
          </div>
        </div>
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        {isLoading ? (
          <AnalysisListSkeleton count={4} />
        ) : sortedAnalyses?.length === 0 ? (
          <AnalysesEmptyState searchQuery={searchQuery} onStartAnalysis={onStartAnalysis} />
        ) : (
          <div className="space-y-4">
            {sortedAnalyses?.map((analysis) => (
              <AnalysisCard
                key={analysis?.id}
                analysis={analysis}
                onViewDetails={onViewDetails}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisTable;